import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HandHeart, Loader2, BadgeCheck, ArrowLeft, Delete, Search } from "lucide-react";
import LazyImage from "@/components/ui/LazyImage";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useLoginDialog } from "@/contexts/LoginDialogContext";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

type Step = "pick" | "pay";

export default function QuickGiveDialog({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const [step, setStep] = useState<Step>("pick");
  const [search, setSearch] = useState("");
  const [org, setOrg] = useState<any | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["quickgive-orgs", search],
    enabled: open && step === "pick",
    queryFn: async () => {
      let q = supabase
        .from("giving_organizations")
        .select("id, name, type, logo_url, is_verified, mission")
        .eq("is_active", true)
        .eq("is_verified", true)
        .order("total_donations", { ascending: false })
        .limit(30);
      if (search.trim()) q = q.ilike("name", `%${search.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  const reset = () => {
    setStep("pick"); setSearch(""); setOrg(null); setAmount(""); setPin("");
  };
  const close = () => { reset(); onOpenChange(false); };

  const pickOrg = (o: any) => {
    if (!user) { openLoginDialog(); return; }
    setOrg(o); setStep("pay");
  };

  const tap = (d: string) => {
    if (pin.length >= 6) return;
    setPin((p) => p + d);
  };
  const back = () => setPin((p) => p.slice(0, -1));

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: "Enter a valid amount", variant: "destructive" }); return;
    }
    if (pin.length !== 6) {
      toast({ title: "Enter your 6-digit passcode", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const { data: res, error } = await supabase.functions.invoke("quickpay-donate", {
        body: {
          organization_id: org.id,
          amount: amt,
          currency: "USD",
          passcode: pin,
          is_anonymous: false,
        },
      });
      if (error) throw error;
      if ((res as any)?.error) throw new Error((res as any).error);
      toast({
        title: "Thank you for giving!",
        description: `Your $${amt.toFixed(2)} donation to ${org.name} is complete.`,
      });
      close();
    } catch (e: any) {
      const msg = e?.message || "Donation failed";
      const code = (e?.context && (e.context as any)?.code) || "";
      if (/no_passcode|Set a withdrawal passcode/i.test(msg)) {
        toast({
          title: "Withdrawal passcode required",
          description: "Set your 6-digit passcode in Accounting → Security first.",
          variant: "destructive",
        });
      } else if (/no_card|saved card/i.test(msg)) {
        toast({
          title: "Wallet insufficient",
          description: "Add funds to your wallet or save a card in Settings.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Donation failed", description: msg, variant: "destructive" });
      }
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === "pay" ? (
              <button onClick={() => setStep("pick")} className="hover:opacity-70"><ArrowLeft className="w-4 h-4" /></button>
            ) : (
              <HandHeart className="w-4 h-4 text-primary" />
            )}
            {step === "pay" ? "Give securely" : "Quick Give"}
          </DialogTitle>
        </DialogHeader>

        {step === "pick" && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search verified organizations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2 mt-2">
              {isLoading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>}
              {!isLoading && (data?.length ?? 0) === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No organizations found.</p>
              )}
              {data?.map((o: any) => (
                <button
                  key={o.id}
                  onClick={() => pickOrg(o)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg border hover:bg-muted text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
                    {o.logo_url
                      ? <LazyImage src={o.logo_url} alt="" className="w-full h-full object-cover" decoding="async" />
                      : <HandHeart className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate flex items-center gap-1">
                      {o.name}
                      {o.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0" />}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize truncate">{o.type}</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground text-center pt-1">
              Funded by your wallet first, then your saved card if needed.
            </p>
          </>
        )}

        {step === "pay" && org && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
                {org.logo_url
                  ? <LazyImage src={org.logo_url} alt="" className="w-full h-full object-cover" decoding="async" />
                  : <HandHeart className="w-5 h-5 text-primary" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{org.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{org.type}</p>
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Amount (USD)</label>
              <Input
                type="number"
                inputMode="decimal"
                min={1}
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-lg font-semibold"
              />
              <div className="flex gap-1.5 mt-2">
                {[5, 10, 25, 50, 100].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(String(v))}
                    className="flex-1 text-xs py-1 rounded border hover:bg-muted"
                  >${v}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">6-digit passcode</label>
              <div className="flex justify-center gap-2 my-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-8 h-10 rounded-md border flex items-center justify-center text-lg font-bold ${
                      pin.length > i ? "bg-primary/10 border-primary" : "bg-muted/30"
                    }`}
                  >
                    {pin.length > i ? "•" : ""}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["1","2","3","4","5","6","7","8","9"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => tap(d)}
                    className="h-11 rounded-lg border bg-card hover:bg-muted text-lg font-semibold"
                  >{d}</button>
                ))}
                <button
                  type="button"
                  onClick={() => setPin("")}
                  className="h-11 rounded-lg border bg-card hover:bg-muted text-xs"
                >Clear</button>
                <button
                  type="button"
                  onClick={() => tap("0")}
                  className="h-11 rounded-lg border bg-card hover:bg-muted text-lg font-semibold"
                >0</button>
                <button
                  type="button"
                  onClick={back}
                  className="h-11 rounded-lg border bg-card hover:bg-muted flex items-center justify-center"
                  aria-label="Backspace"
                ><Delete className="w-4 h-4" /></button>
              </div>
            </div>

            <Button
              className="w-full"
              variant="hero"
              disabled={submitting || pin.length !== 6 || !amount}
              onClick={submit}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Give $${Number(amount || 0).toFixed(2)}`}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Charged from your wallet first, then your saved card if needed.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}